import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChevronDown, ChevronRight, Loader2, Lock, Search } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Permission } from '@metron/shared';
import type {
  ChannelSummary,
  CustomerNotificationSummary,
  NotificationChannel,
  NotificationDetail,
  NotificationState,
  UserNotificationSummary,
} from '@metron/shared';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import { Input } from '../components/ui/input.tsx';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import { usePermissions } from '../hooks/usePermissions.ts';
import {
  type ChannelFilter,
  useCustomerUsers,
  useFailuresByReason,
  useNotificationFailures,
  useNotificationsByCustomer,
  useNotificationsByRecipient,
  useNotificationSummary,
  useNotificationTrend,
  useUserNotifications,
} from '../hooks/useNotificationOverview.ts';

const STATE_COLORS: Record<NotificationState, string> = {
  delivered: '#10b981',
  failed: '#ef4444',
  queued: '#f59e0b',
};

// Timestamps render in the viewer's local zone (toLocaleString); label the
// "When" columns with that zone so the times aren't ambiguous.
const LOCAL_TZ_ABBR = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
  .formatToParts(new Date())
  .find((p) => p.type === 'timeZoneName')?.value ?? 'local';

function alertLabel(category: string | null, subtype: string | null): string {
  if (!category) return 'Non-alert';
  return subtype ? `${category} / ${subtype}` : category;
}

const CATEGORY_COLORS: Record<string, string> = {
  Leak: '#ef4444',
  'High use': '#a855f7',
  'Missed read': '#f97316',
  'Zero use': '#3b82f6',
  Backflow: '#eab308',
  'Low temperature': '#06b6d4',
};

function categoryColor(category: string | null): string {
  if (!category) return '#94a3b8';
  return CATEGORY_COLORS[category] ?? '#8b5cf6';
}

// dot color prioritizes problem states, else the alert category
function dotColor(n: NotificationDetail): string {
  if (n.currentState === 'failed') return '#ef4444';
  if (n.currentState === 'queued') return '#f59e0b';
  return categoryColor(n.alertCategory);
}

const DAY_MS = 24 * 60 * 60 * 1000;

interface TimelineLane {
  key: string;
  sensorId: number | null;
  label: string;
  dots: NotificationDetail[];
}

/**
 * Swimlane timeline: one lane per meter (sensor), each notification a dot placed
 * by time. Makes over-messaging patterns obvious — a dense lane = many
 * messages about one meter / re-sent alerts; many lanes = many meters subscribed.
 */
function NotificationTimeline(
  { notifications, days }: { notifications: NotificationDetail[]; days: number },
) {
  const now = Date.now();
  const start = now - days * DAY_MS;
  const span = Math.max(1, now - start);

  const lanes = useMemo<TimelineLane[]>(() => {
    const map = new Map<string, Omit<TimelineLane, 'label'>>();
    for (const n of notifications) {
      const key = n.sensorId ? `s${n.sensorId}` : 'non-alert';
      let lane = map.get(key);
      if (!lane) {
        lane = { key, sensorId: n.sensorId, dots: [] };
        map.set(key, lane);
      }
      lane.dots.push(n);
    }
    // TODO(MET-2971): lanes are labelled by their busiest-first rank rather than by sensor
    // ID, which is an internal identifier we don't surface. The meter's device ID would be a
    // more useful label — swap it in once get-notification-user-detail.sql joins `sensor`.
    //
    // Numbered in a loop rather than in .map() so the counter skips the single
    // non-alert lane without leaving a gap in the sequence.
    const labelled: TimelineLane[] = [];
    let meterNo = 0;
    for (const lane of [...map.values()].sort((a, b) => b.dots.length - a.dots.length)) {
      if (lane.sensorId) meterNo++;
      labelled.push({ ...lane, label: lane.sensorId ? `Meter ${meterNo}` : 'Non-alert' });
    }
    return labelled;
  }, [notifications]);

  const categoriesPresent = useMemo(() => {
    const set = new Set<string>();
    for (const n of notifications) if (n.alertCategory) set.add(n.alertCategory);
    return [...set];
  }, [notifications]);

  if (notifications.length === 0) {
    return <p className='text-muted-foreground text-sm py-4'>No notifications in this window</p>;
  }

  const leftPct = (iso: string) => {
    const t = Date.parse(iso);
    return Math.min(100, Math.max(0, ((t - start) / span) * 100));
  };
  const ticks = Array.from({ length: 5 }, (_, i) => ({
    left: (i / 4) * 100,
    label: new Date(start + (span * i) / 4).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    }),
  }));
  const busiest = lanes[0];

  return (
    <div className='space-y-2'>
      <div className='flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground'>
        <span>
          {notifications.length} notifications · {lanes.length} meter{lanes.length === 1 ? '' : 's'}
          {busiest && busiest.sensorId ? ` · busiest lane ×${busiest.dots.length}` : ''}
        </span>
        <span className='flex flex-wrap items-center gap-x-3 gap-y-1'>
          {categoriesPresent.map((cat) => (
            <span key={cat} className='flex items-center gap-1'>
              <span
                className='inline-block h-2 w-2 rounded-full'
                style={{ backgroundColor: categoryColor(cat) }}
              />
              {cat}
            </span>
          ))}
        </span>
      </div>

      {
        /* date axis (aligned with the lane tracks, which start after the 10rem label
          gutter; the inner inset matches the dot inset so ticks line up with dots) */
      }
      <div className='relative ml-40 h-4'>
        <div className='absolute inset-x-2 inset-y-0'>
          {ticks.map((t, i) => (
            <span
              key={i}
              className='absolute -translate-x-1/2 text-[10px] text-muted-foreground'
              style={{ left: `${t.left}%` }}
            >
              {t.label}
            </span>
          ))}
        </div>
      </div>

      <div className='space-y-1'>
        {lanes.map((lane) => (
          <div key={lane.key} className='flex items-center'>
            <div className='w-40 shrink-0 truncate pr-2 text-sm'>
              {lane.sensorId
                ? (
                  <Link
                    to={`/meter-inspector/${lane.sensorId}`}
                    className='text-blue-600 hover:underline dark:text-blue-400'
                  >
                    {lane.label}
                  </Link>
                )
                : lane.label}
              <span className='ml-1 text-xs text-muted-foreground'>×{lane.dots.length}</span>
            </div>
            <div className='relative h-6 min-w-0 flex-1 rounded bg-muted/40'>
              <div className='absolute inset-x-2 inset-y-0'>
                {lane.dots.map((d, i) => (
                  <span
                    key={i}
                    title={`${new Date(d.eventCreatedAt).toLocaleString()}\n${
                      alertLabel(d.alertCategory, d.alertSubtype)
                    } · ${d.channel} · ${d.currentState}`}
                    className='absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-1 ring-background'
                    style={{ left: `${leftPct(d.eventCreatedAt)}%`, backgroundColor: dotColor(d) }}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function stateBadge(state: NotificationState) {
  const variant = state === 'delivered'
    ? 'default'
    : state === 'failed'
    ? 'destructive'
    : 'secondary';
  return <Badge variant={variant}>{state}</Badge>;
}

const EMPTY_CHANNEL = (channel: NotificationChannel): ChannelSummary => ({
  channel,
  total: 0,
  delivered: 0,
  failed: 0,
  queued: 0,
  stuck: 0,
  deliveryRate: 0,
  failureRate: 0,
});

function ChannelCard({ summary, highlight }: { summary: ChannelSummary; highlight?: boolean }) {
  const label = summary.channel.toUpperCase();
  return (
    <Card className={highlight ? 'border-blue-400 dark:border-blue-500' : undefined}>
      <CardHeader className='pb-2'>
        <CardTitle className='flex items-center justify-between text-base'>
          <span>{label}</span>
          <span className='text-2xl font-bold'>{summary.total}</span>
        </CardTitle>
        <CardDescription>
          {summary.total > 0
            ? `${summary.deliveryRate}% delivered · ${summary.failureRate}% failed`
            : (highlight ? 'No email sent yet in this window' : 'No notifications in this window')}
        </CardDescription>
      </CardHeader>
      <CardContent className='flex flex-wrap gap-2'>
        <Badge variant='default'>{summary.delivered} delivered</Badge>
        <Badge variant='destructive'>{summary.failed} failed</Badge>
        <Badge variant='secondary'>{summary.queued} queued</Badge>
        {summary.stuck > 0 && <Badge variant='destructive'>{summary.stuck} stuck</Badge>}
      </CardContent>
    </Card>
  );
}

function Spinner({ label }: { label: string }) {
  return (
    <div className='h-[200px] flex items-center justify-center'>
      <div className='flex flex-col items-center gap-2'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
        <p className='text-muted-foreground'>{label}</p>
      </div>
    </div>
  );
}

/** Right-aligned numeric column for the recipient/customer tables. */
function numericColumn<T>(key: string, title: string): ColumnDef<T> {
  return {
    id: key,
    accessorFn: (row) => (row as Record<string, unknown>)[key] as number,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={title} className='justify-end' />
    ),
    cell: ({ row }) => <div className='text-right tabular-nums'>{row.getValue(key) as number}</div>,
  };
}

function buildCustomerColumns(canViewPII: boolean): ColumnDef<CustomerNotificationSummary>[] {
  return [
    {
      id: 'customerName',
      accessorFn: (r) => r.customerName ?? 'Non-alert / Unattributed',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Customer' />,
      cell: ({ row }) => {
        const c = row.original;
        const drillable = canViewPII && c.customerId !== null;
        return (
          <span className='font-medium'>
            {c.customerName ?? 'Non-alert / Unattributed'}
            {drillable && <span className='ml-2 text-xs text-blue-500'>view recipients →</span>}
          </span>
        );
      },
    },
    numericColumn<CustomerNotificationSummary>('total', 'Total'),
    numericColumn<CustomerNotificationSummary>('delivered', 'Delivered'),
    numericColumn<CustomerNotificationSummary>('failed', 'Failed'),
    numericColumn<CustomerNotificationSummary>('sms', 'SMS'),
    numericColumn<CustomerNotificationSummary>('email', 'Email'),
  ];
}

function buildRecipientColumns(): ColumnDef<UserNotificationSummary>[] {
  return [
    {
      id: 'name',
      accessorFn: (r) => r.name ?? `User ${r.userAccountId}`,
      header: ({ column }) => <DataTableColumnHeader column={column} title='Recipient' />,
      cell: ({ row }) => (
        <span className='font-medium'>
          {row.original.name ?? `User ${row.original.userAccountId}`}
          <span className='ml-2 text-xs text-blue-500'>timeline →</span>
        </span>
      ),
    },
    {
      accessorKey: 'email',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Email' />,
      cell: ({ row }) => <span className='break-all'>{row.original.email ?? '—'}</span>,
    },
    numericColumn<UserNotificationSummary>('total', 'Total'),
    numericColumn<UserNotificationSummary>('delivered', 'Delivered'),
    numericColumn<UserNotificationSummary>('failed', 'Failed'),
  ];
}

export function NotificationOverviewPage() {
  const { hasPermission } = usePermissions();
  const canViewPII = hasPermission(Permission.VIEW_USERS);

  const [days, setDays] = useState(7);
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const [selectedCustomer, setSelectedCustomer] = useState<
    { id: number; name: string } | null
  >(null);
  const [selectedUser, setSelectedUser] = useState<{ id: number; name: string } | null>(null);
  // Independent timespan for the per-user timeline modal.
  const [timelineDays, setTimelineDays] = useState(30);
  // Notification rows (by key) expanded to show their full lifecycle.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Search + sort/paginate state for the customer and recipient tables.
  const [customerSearch, setCustomerSearch] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const customerTableState = useTableUrlState({
    prefix: 'cust',
    defaultSorting: [{ id: 'total', desc: true }],
  });
  const recipientTableState = useTableUrlState({
    prefix: 'rcpt',
    defaultSorting: [{ id: 'total', desc: true }],
  });
  const customerColumns = useMemo(() => buildCustomerColumns(canViewPII), [canViewPII]);
  const recipientColumns = useMemo(() => buildRecipientColumns(), []);

  const summaryQ = useNotificationSummary(days, channel);
  // Hourly buckets up to a 7-day window (to show time-of-day patterns); daily beyond.
  const trendInterval: 'hour' | 'day' = days <= 7 ? 'hour' : 'day';
  const trendQ = useNotificationTrend(days, channel, trendInterval);
  const byCustomerQ = useNotificationsByCustomer(days, channel);
  const byRecipientQ = useNotificationsByRecipient(days, channel, canViewPII);
  const reasonsQ = useFailuresByReason(days, channel);
  const failuresQ = useNotificationFailures(days, channel, canViewPII);

  const customerUsersQ = useCustomerUsers(
    selectedCustomer?.id ?? null,
    days,
    channel,
    canViewPII && !!selectedCustomer,
  );
  const userNotificationsQ = useUserNotifications(
    selectedUser?.id ?? null,
    timelineDays,
    channel,
    canViewPII && !!selectedUser,
  );

  const sms = summaryQ.data?.channels.find((c) => c.channel === 'sms') ?? EMPTY_CHANNEL('sms');
  const email = summaryQ.data?.channels.find((c) => c.channel === 'email') ??
    EMPTY_CHANNEL('email');
  const totals = summaryQ.data?.totals;

  // Aggregate the trend across channels per bucket, then zero-fill every empty
  // bucket in the window so the area chart drops to 0 between sends (rather than
  // connecting sparse points into a flat line) and is evenly spaced in time.
  const trendData = useMemo(() => {
    const stepMs = trendInterval === 'hour' ? 60 * 60 * 1000 : DAY_MS;
    const sums = new Map<number, { queued: number; delivered: number; failed: number }>();
    for (const p of trendQ.data?.trend ?? []) {
      // time_bucket boundaries are epoch-aligned (UTC); floor to the step to match.
      const key = Math.floor(Date.parse(p.bucket) / stepMs) * stepMs;
      const e = sums.get(key) ?? { queued: 0, delivered: 0, failed: 0 };
      e.queued += p.queued;
      e.delivered += p.delivered;
      e.failed += p.failed;
      sums.set(key, e);
    }
    const now = Date.now();
    const end = Math.floor(now / stepMs) * stepMs;
    const startBucket = Math.floor((now - days * DAY_MS) / stepMs) * stepMs;
    const points: Array<
      { label: string; queued: number; delivered: number; failed: number }
    > = [];
    for (let t = startBucket; t <= end; t += stepMs) {
      const e = sums.get(t) ?? { queued: 0, delivered: 0, failed: 0 };
      points.push({
        label: new Date(t).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          ...(trendInterval === 'hour' ? { hour: 'numeric' } : {}),
        }),
        ...e,
      });
    }
    return points;
  }, [trendQ.data, trendInterval, days]);

  const openCustomer = (id: number | null, name: string | null) => {
    if (!canViewPII || id === null) return;
    setSelectedCustomer({ id, name: name ?? `Customer ${id}` });
  };

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    const rows = byCustomerQ.data?.customers ?? [];
    if (!q) return rows;
    return rows.filter((c) =>
      (c.customerName ?? 'Non-alert / Unattributed').toLowerCase().includes(q)
    );
  }, [byCustomerQ.data, customerSearch]);

  const filteredRecipients = useMemo(() => {
    const q = recipientSearch.trim().toLowerCase();
    const rows = byRecipientQ.data?.users ?? [];
    if (!q) return rows;
    return rows.filter((u) =>
      (u.name ?? '').toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q)
    );
  }, [byRecipientQ.data, recipientSearch]);

  return (
    <div className='p-6 space-y-8'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>Notification Overview</h1>
          <p className='text-muted-foreground'>
            SMS &amp; email delivery health, recipients, and failures
          </p>
        </div>
        <div className='flex gap-2'>
          <Select value={channel} onValueChange={(v) => setChannel(v as ChannelFilter)}>
            <SelectTrigger className='w-[130px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All channels</SelectItem>
              <SelectItem value='sms'>SMS</SelectItem>
              <SelectItem value='email'>Email</SelectItem>
            </SelectContent>
          </Select>
          <Select value={days.toString()} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className='w-[130px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='2'>Last 2 days</SelectItem>
              <SelectItem value='7'>Last 7 days</SelectItem>
              <SelectItem value='14'>Last 14 days</SelectItem>
              <SelectItem value='30'>Last 30 days</SelectItem>
              <SelectItem value='90'>Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* View 1: Volume & health */}
      <section className='space-y-4'>
        <h2 className='text-xl font-semibold'>Volume &amp; health</h2>
        {summaryQ.isLoading ? <Spinner label='Loading summary...' /> : (
          <>
            <div className='grid gap-4 md:grid-cols-3'>
              {(channel === 'all' || channel === 'sms') && <ChannelCard summary={sms} />}
              {(channel === 'all' || channel === 'email') && (
                <ChannelCard summary={email} highlight />
              )}
              {totals && (
                <Card>
                  <CardHeader className='pb-2'>
                    <CardTitle className='flex items-center justify-between text-base'>
                      <span>ALL</span>
                      <span className='text-2xl font-bold'>{totals.total}</span>
                    </CardTitle>
                    <CardDescription>
                      {totals.total > 0
                        ? `${totals.deliveryRate}% delivered · ${totals.failureRate}% failed`
                        : 'No notifications in this window'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className='flex flex-wrap gap-2'>
                    <Badge variant='default'>{totals.delivered} delivered</Badge>
                    <Badge variant='destructive'>{totals.failed} failed</Badge>
                    <Badge variant='secondary'>{totals.queued} queued</Badge>
                    {totals.stuck > 0 && (
                      <Badge variant='destructive'>{totals.stuck} stuck (no terminal event)</Badge>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Volume by {trendInterval}</CardTitle>
                <CardDescription>
                  {trendInterval === 'hour' ? 'Hourly' : 'Daily'} notification volume over the last
                  {' '}
                  {days} days
                  {trendInterval === 'hour' ? ' — shows time-of-day patterns' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {trendData.length === 0
                  ? (
                    <p className='text-muted-foreground py-8 text-center'>
                      No notifications in this window
                    </p>
                  )
                  : (
                    <ResponsiveContainer width='100%' height={300}>
                      {trendInterval === 'hour'
                        ? (
                          <AreaChart data={trendData}>
                            <CartesianGrid strokeDasharray='3 3' className='stroke-muted' />
                            <XAxis dataKey='label' fontSize={11} minTickGap={48} />
                            <YAxis allowDecimals={false} fontSize={12} />
                            <Tooltip />
                            <Legend />
                            <Area
                              type='monotone'
                              stackId='s'
                              dataKey='delivered'
                              stroke={STATE_COLORS.delivered}
                              fill={STATE_COLORS.delivered}
                              fillOpacity={0.3}
                              dot={{ r: 1.5 }}
                              name='Delivered'
                            />
                            <Area
                              type='monotone'
                              stackId='s'
                              dataKey='failed'
                              stroke={STATE_COLORS.failed}
                              fill={STATE_COLORS.failed}
                              fillOpacity={0.3}
                              dot={{ r: 1.5 }}
                              name='Failed'
                            />
                            <Area
                              type='monotone'
                              stackId='s'
                              dataKey='queued'
                              stroke={STATE_COLORS.queued}
                              fill={STATE_COLORS.queued}
                              fillOpacity={0.3}
                              dot={{ r: 1.5 }}
                              name='Queued'
                            />
                          </AreaChart>
                        )
                        : (
                          <BarChart data={trendData}>
                            <CartesianGrid strokeDasharray='3 3' className='stroke-muted' />
                            <XAxis dataKey='label' fontSize={11} minTickGap={24} />
                            <YAxis allowDecimals={false} fontSize={12} />
                            <Tooltip />
                            <Legend />
                            <Bar
                              stackId='s'
                              dataKey='delivered'
                              fill={STATE_COLORS.delivered}
                              name='Delivered'
                            />
                            <Bar
                              stackId='s'
                              dataKey='failed'
                              fill={STATE_COLORS.failed}
                              name='Failed'
                            />
                            <Bar
                              stackId='s'
                              dataKey='queued'
                              fill={STATE_COLORS.queued}
                              name='Queued'
                            />
                          </BarChart>
                        )}
                    </ResponsiveContainer>
                  )}
              </CardContent>
            </Card>
          </>
        )}
      </section>

      {/* View 2: Recipients */}
      <section className='space-y-4'>
        <h2 className='text-xl font-semibold'>Recipients</h2>
        <Card>
          <CardHeader>
            <CardTitle>Notifications by customer</CardTitle>
            <CardDescription>
              Who is receiving notifications. {canViewPII
                ? 'Select a customer to drill into its users.'
                : 'User-level detail requires the View Users permission.'}
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            {byCustomerQ.isLoading ? <Spinner label='Loading customers...' /> : (
              <>
                <div className='relative'>
                  <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
                  <Input
                    placeholder='Search customers by name...'
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className='pl-8'
                  />
                </div>
                <DataTable
                  columns={customerColumns}
                  data={filteredCustomers}
                  sorting={customerTableState.sorting}
                  onSortingChange={customerTableState.onSortingChange}
                  pagination={customerTableState.pagination}
                  onPaginationChange={customerTableState.onPaginationChange}
                  onRowClick={canViewPII
                    ? (c) =>
                      openCustomer(c.customerId, c.customerName)
                    : undefined}
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Notifications by recipient</CardTitle>
            <CardDescription>
              {canViewPII
                ? 'Individual recipients across all customers. Select a recipient to see their timeline.'
                : 'Recipient-level detail requires the View Users permission.'}
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            {!canViewPII
              ? (
                <div className='flex items-center gap-2 py-6 text-muted-foreground'>
                  <Lock className='h-4 w-4' />
                  Recipient names and emails require the View Users permission.
                </div>
              )
              : byRecipientQ.isLoading
              ? <Spinner label='Loading recipients...' />
              : (
                <>
                  <div className='relative'>
                    <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
                    <Input
                      placeholder='Search recipients by name or email...'
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      className='pl-8'
                    />
                  </div>
                  <DataTable
                    columns={recipientColumns}
                    data={filteredRecipients}
                    sorting={recipientTableState.sorting}
                    onSortingChange={recipientTableState.onSortingChange}
                    pagination={recipientTableState.pagination}
                    onPaginationChange={recipientTableState.onPaginationChange}
                    onRowClick={(u) =>
                      setSelectedUser({
                        id: u.userAccountId,
                        name: u.name ?? u.email ?? `User ${u.userAccountId}`,
                      })}
                  />
                </>
              )}
          </CardContent>
        </Card>
      </section>

      {/* View 3: Failures */}
      <section className='space-y-4'>
        <h2 className='text-xl font-semibold'>Failures</h2>
        <Card>
          <CardHeader>
            <CardTitle>By reason</CardTitle>
            <CardDescription>Failure counts grouped by error code and channel</CardDescription>
          </CardHeader>
          <CardContent>
            {reasonsQ.isLoading ? <Spinner label='Loading failures...' /> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Channel</TableHead>
                    <TableHead>Error type</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead className='text-right'>Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(reasonsQ.data?.reasons ?? []).length === 0
                    ? (
                      <TableRow>
                        <TableCell colSpan={4} className='text-center text-muted-foreground'>
                          No failures in this window 🎉
                        </TableCell>
                      </TableRow>
                    )
                    : reasonsQ.data?.reasons.map((r, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Badge variant='outline'>{r.channel}</Badge>
                        </TableCell>
                        <TableCell className='font-mono text-sm'>{r.errorType}</TableCell>
                        <TableCell className='max-w-md truncate'>{r.errorMessage}</TableCell>
                        <TableCell className='text-right'>{r.count}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {canViewPII
          ? (
            <Card>
              <CardHeader>
                <CardTitle>Failed notifications</CardTitle>
                <CardDescription>Every failure with recipient and reason</CardDescription>
              </CardHeader>
              <CardContent>
                {failuresQ.isLoading ? <Spinner label='Loading failed notifications...' /> : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When ({LOCAL_TZ_ABBR})</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Channel</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(failuresQ.data?.failures ?? []).length === 0
                        ? (
                          <TableRow>
                            <TableCell colSpan={5} className='text-center text-muted-foreground'>
                              No failures in this window 🎉
                            </TableCell>
                          </TableRow>
                        )
                        : failuresQ.data?.failures.map((f, i) => (
                          <TableRow key={i}>
                            <TableCell className='whitespace-nowrap text-sm'>
                              {new Date(f.eventCreatedAt).toLocaleString()}
                            </TableCell>
                            <TableCell>{f.customerName ?? '—'}</TableCell>
                            <TableCell className='font-mono text-sm'>
                              {f.name ?? f.email ?? f.recipientAddress}
                            </TableCell>
                            <TableCell>
                              <Badge variant='outline'>{f.channel}</Badge>
                            </TableCell>
                            <TableCell className='max-w-xs truncate'>
                              <span className='font-mono text-xs'>{f.errorType}</span>{' '}
                              {f.errorMessage}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )
          : (
            <Card>
              <CardContent className='flex items-center gap-2 py-6 text-muted-foreground'>
                <Lock className='h-4 w-4' />
                Detailed failures (with recipient addresses) require the View Users permission.
              </CardContent>
            </Card>
          )}
      </section>

      {/* Customer → users drill-down */}
      <Dialog
        open={!!selectedCustomer}
        onOpenChange={(o) => !o && setSelectedCustomer(null)}
      >
        <DialogContent className='flex max-h-[85vh] w-[95vw] max-w-2xl flex-col overflow-hidden'>
          <DialogHeader>
            <DialogTitle>Users — {selectedCustomer?.name}</DialogTitle>
          </DialogHeader>
          {customerUsersQ.isLoading
            ? <Spinner label='Loading users...' />
            : (
              <div className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden'>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead className='text-right'>Total</TableHead>
                      <TableHead className='text-right'>Delivered</TableHead>
                      <TableHead className='text-right'>Failed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(customerUsersQ.data?.users ?? []).length === 0
                      ? (
                        <TableRow>
                          <TableCell colSpan={5} className='text-center text-muted-foreground'>
                            No users found
                          </TableCell>
                        </TableRow>
                      )
                      : customerUsersQ.data?.users.map((u) => (
                        <TableRow
                          key={u.userAccountId}
                          className='cursor-pointer hover:bg-muted/50'
                          onClick={() =>
                            setSelectedUser({
                              id: u.userAccountId,
                              name: u.name ?? u.email ?? `User ${u.userAccountId}`,
                            })}
                        >
                          <TableCell className='font-medium'>
                            {u.name ?? `User ${u.userAccountId}`}
                          </TableCell>
                          <TableCell className='break-all'>{u.email ?? '—'}</TableCell>
                          <TableCell className='text-right'>{u.total}</TableCell>
                          <TableCell className='text-right'>{u.delivered}</TableCell>
                          <TableCell className='text-right'>{u.failed}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            )}
        </DialogContent>
      </Dialog>

      {/* User → notifications drill-down */}
      <Dialog
        open={!!selectedUser}
        onOpenChange={(o) => {
          if (!o) {
            setSelectedUser(null);
            setExpandedRows(new Set());
          }
        }}
      >
        <DialogContent className='flex max-h-[90vh] w-[95vw] max-w-5xl flex-col overflow-hidden'>
          <DialogHeader>
            <div className='flex items-center justify-between gap-4 pr-6'>
              <DialogTitle>Notifications — {selectedUser?.name}</DialogTitle>
              <Select
                value={timelineDays.toString()}
                onValueChange={(v) => setTimelineDays(Number(v))}
              >
                <SelectTrigger className='w-[130px]'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='7'>Last 7 days</SelectItem>
                  <SelectItem value='14'>Last 14 days</SelectItem>
                  <SelectItem value='30'>Last 30 days</SelectItem>
                  <SelectItem value='90'>Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </DialogHeader>
          {userNotificationsQ.isLoading
            ? <Spinner label='Loading notifications...' />
            : (
              <div className='min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden'>
                <NotificationTimeline
                  notifications={userNotificationsQ.data?.notifications ?? []}
                  days={timelineDays}
                />
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When ({LOCAL_TZ_ABBR})</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Channel</TableHead>
                      <TableHead>Alert</TableHead>
                      <TableHead>State</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(userNotificationsQ.data?.notifications ?? []).length === 0
                      ? (
                        <TableRow>
                          <TableCell colSpan={5} className='text-center text-muted-foreground'>
                            No notifications found
                          </TableCell>
                        </TableRow>
                      )
                      : userNotificationsQ.data?.notifications.map((n, i) => {
                        const rowKey = `${n.channel}|${n.eventCreatedAt}|${i}`;
                        const isExpanded = expandedRows.has(rowKey);
                        const toggle = () =>
                          setExpandedRows((prev) => {
                            const next = new Set(prev);
                            if (next.has(rowKey)) next.delete(rowKey);
                            else next.add(rowKey);
                            return next;
                          });
                        return (
                          <Fragment key={rowKey}>
                            <TableRow className='cursor-pointer hover:bg-muted/50' onClick={toggle}>
                              <TableCell className='whitespace-nowrap text-sm'>
                                <span className='inline-flex items-center gap-1'>
                                  {isExpanded
                                    ? <ChevronDown className='h-3.5 w-3.5 text-muted-foreground' />
                                    : (
                                      <ChevronRight className='h-3.5 w-3.5 text-muted-foreground' />
                                    )}
                                  {new Date(n.eventCreatedAt).toLocaleString()}
                                </span>
                              </TableCell>
                              <TableCell className='break-all font-mono text-sm'>
                                {n.recipientAddress}
                              </TableCell>
                              <TableCell>
                                <Badge variant='outline'>{n.channel}</Badge>
                              </TableCell>
                              <TableCell>
                                {n.sensorId
                                  ? (
                                    <Link
                                      to={`/meter-inspector/${n.sensorId}`}
                                      className='text-blue-600 hover:underline dark:text-blue-400'
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {alertLabel(n.alertCategory, n.alertSubtype)}
                                      <span className='ml-1 text-xs text-muted-foreground'>↗</span>
                                    </Link>
                                  )
                                  : alertLabel(n.alertCategory, n.alertSubtype)}
                              </TableCell>
                              <TableCell>{stateBadge(n.currentState)}</TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow className='hover:bg-transparent'>
                                <TableCell colSpan={5} className='bg-muted/30'>
                                  <div className='space-y-1 py-1 pl-6'>
                                    <p className='text-xs font-medium text-muted-foreground'>
                                      Lifecycle ({LOCAL_TZ_ABBR})
                                    </p>
                                    {n.events.length === 0
                                      ? (
                                        <p className='text-sm text-muted-foreground'>
                                          No event history
                                        </p>
                                      )
                                      : n.events.map((ev, j) => (
                                        <div
                                          key={j}
                                          className='flex items-center gap-2 text-sm'
                                        >
                                          {stateBadge(ev.eventType)}
                                          <span className='font-mono text-xs'>
                                            {new Date(ev.createdAt).toLocaleString()}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </Fragment>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
